export const convertFileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// Decodifica el archivo respetando la orientación EXIF. Las fotos tomadas con
// celular vienen rotadas por metadatos: sin esto se subirían acostadas.
const decodeImage = async file => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
      // Safari antiguo no acepta la opción; se usa el camino alterno.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No fue posible leer la imagen.'));
    };
    img.src = url;
  });
};

/**
 * Redimensiona y recomprime una imagen antes de subirla.
 *
 * Una foto de celular ronda los 4 MB; a 1600 px de ancho y calidad 0.8 baja a
 * unos 300 KB, resolución más que suficiente para lo que muestra el mapa.
 *
 * Devuelve el archivo original, sin tocar, si no es una imagen o si el
 * resultado no pesa menos que el original.
 */
export const compressImage = async (
  file,
  { maxWidth = 1600, maxHeight = 1600, quality = 0.8, minSize = 1024 * 1024 } = {}
) => {
  if (!(file instanceof File) || !file.type?.startsWith('image/')) return file;
  // Los GIF pueden ser animados y el canvas se quedaría solo con el primer cuadro.
  if (file.type === 'image/gif') return file;

  let source;
  try {
    source = await decodeImage(file);
  } catch (e) {
    return file;
  }

  const width = source.width;
  const height = source.height;
  if (!width || !height) return file;

  const scale = Math.min(1, maxWidth / width, maxHeight / height);

  // Si no hay que reducir la imagen y el archivo ya es liviano, se devuelve
  // intacto: recomprimir un JPEG solo degradaría la calidad a cambio de una
  // ganancia mínima. Las fotos que ya llegan reducidas pasan sin tocarse.
  if (scale === 1 && file.size <= minSize) {
    if (typeof source.close === 'function') source.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (typeof source.close === 'function') source.close();

  const blob = await new Promise(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', quality)
  );
  if (!blob || blob.size >= file.size) return file;

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

export const formatBytes = bytes => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
